// Procedural ambience engine (M12). No sound assets: wind is filtered noise
// with gust modulation, birds are little sine sweeps, waves a slow-swelling
// low noise, insects high trills, creaks rare falling groans, drone a detuned
// low pair. The per-biome/per-mood parameters come from the agent-authored
// catalog (assets/map10k/catalog/ambience.json) with a built-in fallback.
// Shares GameAudio's context (user-gesture rule handled there).

const FALLBACK = {
  biomes: {
    sea: { wind: { level: 0.55, freq: 500, gustiness: 0.5 }, rustle: 0, birds: { density: 0.1, pitch: [1800, 2600], kinds: 1 }, insects: 0, waves: 0.8, creaks: 0, drone: 0 },
    beach: { wind: { level: 0.5, freq: 500, gustiness: 0.5 }, rustle: 0.1, birds: { density: 0.25, pitch: [1800, 2800], kinds: 1 }, insects: 0.05, waves: 0.7, creaks: 0, drone: 0 },
    meadow: { wind: { level: 0.45, freq: 420, gustiness: 0.4 }, rustle: 0.3, birds: { density: 0.5, pitch: [2200, 4200], kinds: 3 }, insects: 0.3, waves: 0, creaks: 0, drone: 0 },
    farmland: { wind: { level: 0.45, freq: 400, gustiness: 0.4 }, rustle: 0.25, birds: { density: 0.35, pitch: [2000, 3800], kinds: 2 }, insects: 0.3, waves: 0, creaks: 0.05, drone: 0 },
    forest_birch: { wind: { level: 0.4, freq: 300, gustiness: 0.5 }, rustle: 0.5, birds: { density: 0.7, pitch: [2400, 4600], kinds: 4 }, insects: 0.15, waves: 0, creaks: 0, drone: 0 },
    forest_pine: { wind: { level: 0.45, freq: 220, gustiness: 0.5 }, rustle: 0.45, birds: { density: 0.25, pitch: [2000, 3400], kinds: 2 }, insects: 0.1, waves: 0, creaks: 0, drone: 0.05 },
    forest_mixed: { wind: { level: 0.42, freq: 260, gustiness: 0.5 }, rustle: 0.5, birds: { density: 0.5, pitch: [2200, 4200], kinds: 3 }, insects: 0.12, waves: 0, creaks: 0, drone: 0 },
    swamp: { wind: { level: 0.3, freq: 300, gustiness: 0.3 }, rustle: 0.25, birds: { density: 0.2, pitch: [1600, 3000], kinds: 2 }, insects: 0.6, waves: 0, creaks: 0, drone: 0.15 },
    rock: { wind: { level: 0.6, freq: 700, gustiness: 0.7 }, rustle: 0.05, birds: { density: 0.08, pitch: [2600, 3600], kinds: 1 }, insects: 0, waves: 0, creaks: 0, drone: 0.08 },
    alpine: { wind: { level: 0.7, freq: 900, gustiness: 0.8 }, rustle: 0, birds: { density: 0.04, pitch: [2600, 3600], kinds: 1 }, insects: 0, waves: 0, creaks: 0, drone: 0.12 },
    urban: { wind: { level: 0.4, freq: 350, gustiness: 0.6 }, rustle: 0.08, birds: { density: 0.12, pitch: [2000, 3600], kinds: 2 }, insects: 0.05, waves: 0, creaks: 0.5, drone: 0.08 },
  },
  moods: {
    overcast: { windMul: 1.1, birdsMul: 0.6, insectsMul: 0.7, creaksMul: 1.1, level: 1 },
    partly: { windMul: 1, birdsMul: 1, insectsMul: 1, creaksMul: 1, level: 1 },
    clear: { windMul: 0.85, birdsMul: 1.2, insectsMul: 1.1, creaksMul: 0.9, level: 1 },
    misty: { windMul: 0.7, birdsMul: 0.3, insectsMul: 0.5, creaksMul: 1.2, level: 0.9 },
    sunset: { windMul: 0.9, birdsMul: 0.7, insectsMul: 1.3, creaksMul: 1, level: 1 },
    night: { windMul: 0.8, birdsMul: 0.02, insectsMul: 1.6, creaksMul: 1.3, level: 0.9, owl: 0.3 },
  },
};

function noiseBuffer(c, seconds = 2) {
  const buf = c.createBuffer(1, c.sampleRate * seconds, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

export class Ambience {
  constructor(audio, biomeKeyAt, sfx = null) {
    this.audio = audio;          // GameAudio — owns ctx/master
    this.biomeKeyAt = biomeKeyAt;
    this.sfx = sfx;              // CC0 sample library — beds crossfade OVER the synth
    this.beds = {};              // role -> {g, started}
    this.cfg = FALLBACK;
    this.mood = 'overcast';
    this.acc = 0;
    this.started = false;
    this.gust = 0.5;
  }

  // Drive a looping sample bed toward `target` gain; lazily created on first
  // audible request. Returns true when the bed is actually carrying sound.
  setBed(role, target, T = 2.5) {
    let h = this.beds[role];
    if (!h) {
      if (target <= 0.015 || !this.sfx?.has(role)) return false;
      h = this.sfx.bed(role);
      if (!h) return false;
      this.beds[role] = h;
    }
    h.g.gain.setTargetAtTime(target, this.audio.ctx.currentTime, T);
    return h.started && target > 0.02;
  }

  async init() {
    try {
      const j = await (await fetch('assets/map10k/catalog/ambience.json')).json();
      if (j.biomes && j.moods) this.cfg = j;
    } catch { /* fallback table */ }
    return this;
  }

  start() {
    const c = this.audio.ctx;
    if (!c || c.state !== 'running') return;
    this.started = true;
    this.master = c.createGain();
    this.master.gain.value = 0.22;
    this.master.connect(this.audio.master);
    const loop = (filterType, freq, gain = 0) => {
      const src = c.createBufferSource();
      src.buffer = noiseBuffer(c);
      src.loop = true;
      const f = c.createBiquadFilter();
      f.type = filterType;
      f.frequency.value = freq;
      const g = c.createGain();
      g.gain.value = gain;
      src.connect(f); f.connect(g); g.connect(this.master);
      src.start();
      return { f, g };
    };
    this.wind = loop('lowpass', 400);
    this.rustle = loop('bandpass', 3200);
    this.waves = loop('lowpass', 550);
    // drone: detuned low pair
    this.droneGain = c.createGain();
    this.droneGain.gain.value = 0;
    for (const det of [0, 3]) {
      const o = c.createOscillator();
      o.type = 'sine';
      o.frequency.value = 54 + det;
      o.connect(this.droneGain);
      o.start();
    }
    this.droneGain.connect(this.master);
  }

  chirp(pitchLo, pitchHi, kinds) {
    const c = this.audio.ctx, t = c.currentTime;
    const kind = Math.floor(Math.random() * Math.max(1, kinds));
    const base = pitchLo + Math.random() * (pitchHi - pitchLo);
    const notes = 2 + (kind % 3);
    for (let n = 0; n < notes; n++) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = 'sine';
      const t0 = t + n * (0.09 + kind * 0.03);
      o.frequency.setValueAtTime(base * (1 + (n % 2) * 0.12), t0);
      o.frequency.exponentialRampToValueAtTime(base * (0.8 + Math.random() * 0.5), t0 + 0.07);
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.12, t0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.09);
      o.connect(g); g.connect(this.master);
      o.start(t0); o.stop(t0 + 0.12);
    }
  }

  owlCall() {
    const c = this.audio.ctx, t = c.currentTime;
    for (const [dt, len] of [[0, 0.28], [0.45, 0.5]]) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(340, t + dt);
      o.frequency.exponentialRampToValueAtTime(300, t + dt + len);
      g.gain.setValueAtTime(0, t + dt);
      g.gain.linearRampToValueAtTime(0.08, t + dt + 0.06);
      g.gain.exponentialRampToValueAtTime(0.001, t + dt + len);
      o.connect(g); g.connect(this.master);
      o.start(t + dt); o.stop(t + dt + len + 0.05);
    }
  }

  creak() {
    const c = this.audio.ctx, t = c.currentTime;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(160 + Math.random() * 120, t);
    o.frequency.exponentialRampToValueAtTime(60 + Math.random() * 40, t + 0.7);
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 500;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.05, t + 0.15);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    o.connect(f); f.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + 1);
  }

  cricket() {
    const c = this.audio.ctx, t = c.currentTime;
    for (let n = 0; n < 4; n++) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = 'square';
      o.frequency.value = 4300 + Math.random() * 500;
      const t0 = t + n * 0.055;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.014, t0 + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.045);
      o.connect(g); g.connect(this.master);
      o.start(t0); o.stop(t0 + 0.05);
    }
  }

  setEnv(e) {
    if (e.moodKey) this.mood = e.moodKey;
  }

  tick() { /* sky target interface — time comes via update */ }

  update(dt, pos) {
    if (!this.started) { this.start(); return; }
    this.acc += dt;
    if (this.acc < 0.8) return;
    const step = this.acc;
    this.acc = 0;
    const c = this.audio.ctx;
    if (!c || c.state !== 'running') return;
    const B = this.cfg.biomes[this.biomeKeyAt(pos.x, pos.z)] || this.cfg.biomes.meadow;
    const M = this.cfg.moods[this.mood] || this.cfg.moods.overcast;
    // gust random walk
    this.gust = Math.max(0.15, Math.min(1, this.gust + (Math.random() - 0.5) * 0.3 * (B.wind.gustiness ?? 0.5)));
    const T = 1.2, t = c.currentTime;
    const windLvl = (B.wind.level ?? 0.4) * (M.windMul ?? 1) * (0.55 + 0.45 * this.gust) * (M.level ?? 1);

    // ---- sample beds (CC0 harvest) crossfade by biome+mood; each bed that is
    // actually sounding ducks its synthetic counterpart below
    const key = this.biomeKeyAt(pos.x, pos.z);
    const night = this.mood === 'night';
    const dusk = this.mood === 'sunset';
    const forest = key.startsWith('forest');
    const windRole = key === 'alpine' || key === 'rock' ? 'bed.wind_mountain'
      : forest ? 'bed.wind_forest' : 'bed.wind_open';
    const bedWind = this.setBed(windRole, windLvl * 0.32);
    for (const r of ['bed.wind_mountain', 'bed.wind_forest', 'bed.wind_open']) {
      if (r !== windRole) this.setBed(r, 0);
    }
    const bedWaves = this.setBed('bed.waves', (B.waves ?? 0) * 0.4);
    const birds = (B.birds?.density ?? 0) * (M.birdsMul ?? 1);
    const bedBirds = this.setBed('bed.birds', night ? 0 : birds * 0.22 * (M.level ?? 1));
    const insects = (B.insects ?? 0) * (M.insectsMul ?? 1);
    const bedCrickets = this.setBed('bed.crickets', (night ? 0.8 : dusk ? 0.35 : 0) * Math.min(1, insects + 0.25) * 0.22);
    this.setBed('bed.swamp', key === 'swamp' ? (night ? 0.3 : 0.15) : 0);
    // the dread layer: a dark drone deep in the woods after dark
    this.setBed('bed.eerie', forest && night ? 0.07 : 0, 5);

    this.wind.g.gain.setTargetAtTime(windLvl * (bedWind ? 0.22 : 0.5), t, T);
    this.wind.f.frequency.setTargetAtTime((B.wind.freq ?? 400) * (0.8 + 0.5 * this.gust), t, T);
    this.rustle.g.gain.setTargetAtTime((B.rustle ?? 0) * windLvl * 0.12, t, T);
    this.waves.g.gain.setTargetAtTime((B.waves ?? 0) * (bedWaves ? 0.08 : 0.4) * (0.6 + 0.4 * Math.sin(t * 0.55)), t, 0.8);
    this.droneGain.gain.setTargetAtTime((B.drone ?? 0) * 0.1 * (M.level ?? 1), t, 2);

    // ---- event one-shots: sample first, synth fallback
    if (Math.random() < birds * step * 0.12 && !night) {
      if (!bedBirds || Math.random() < 0.35) this.chirp(B.birds.pitch[0], B.birds.pitch[1], B.birds.kinds ?? 2);
    }
    if ((key === 'sea' || key === 'beach') && Math.random() < step * 0.05) {
      this.sfx?.play('amb.gull', { vol: 0.1, slice: 3, pan: Math.random() - 0.5 });
    }
    if ((forest || key === 'farmland') && !night && Math.random() < step * 0.025) {
      this.sfx?.play('amb.crow', { vol: 0.09, slice: 2.5, pan: Math.random() - 0.5 });
    }
    if (Math.random() < (B.insects ?? 0) * (M.insectsMul ?? 1) * step * 0.2 && !bedCrickets) this.cricket();
    if (Math.random() < (B.creaks ?? 0) * (M.creaksMul ?? 1) * step * 0.05) {
      const role = Math.random() < 0.4 ? 'amb.door_creak' : Math.random() < 0.5 ? 'amb.creak_metal' : 'amb.creak_wood';
      if (!this.sfx?.play(role, { vol: 0.05 + 0.14 * this.gust, slice: 4, rate: 0.92, pan: Math.random() - 0.5 })) this.creak();
    }
    // lone creaking branches/trunks in the forest when the wind gusts
    if (forest && Math.random() < step * 0.02 * this.gust) {
      this.sfx?.play('amb.creak_wood', { vol: 0.045, slice: 3, rate: 0.85, pan: Math.random() - 0.5 });
    }
    if (Math.random() < (M.owl ?? 0) * step * 0.02) {
      if (!this.sfx?.play('amb.owl', { vol: 0.08, slice: 4, pan: Math.random() - 0.5 })) this.owlCall();
    }
    // very rare, very distant: wolves after dark. Pure dread, no threat (yet).
    if ((night || dusk) && (forest || key === 'meadow') && Math.random() < step * 0.004) {
      this.sfx?.play('amb.wolf', { vol: 0.045, slice: 6, rate: 0.95, pan: Math.random() - 0.5 });
    }
  }
}
